import '@mapbox/mapbox-gl-draw/dist/mapbox-gl-draw.css'
import MapBoxGL, { GeoJSONSource, MapLayerMouseEvent } from 'mapbox-gl'
import { useEffect, useState } from 'react'
import { v4 as UUID } from 'uuid'

import { IGeoJsonData, useMap } from './index'

interface IDrawLayer {
  geoData: IGeoJsonData | undefined
  id?: string
  layerOptions?: any
  onClickLayer?: (event: MapLayerMouseEvent) => void
}

const GeoJsonLayer = (props: IDrawLayer) => {
  const { geoData, layerOptions = {}, id, onClickLayer } = props
  const [objectID] = useState(id || UUID())
  const [layerId] = useState<string>(`geojson-layer-${objectID}`)
  const [sourceId] = useState<string>(`geojson-source-${objectID}`)

  const map = useMap()

  // Unbind
  useEffect(() => {
    return () => {
      if (!map) {
        return
      }

      const mapStyles = map.getStyle()
      if (mapStyles && mapStyles.layers) {
        if (mapStyles.layers.find((l) => l.id === layerId)) {
          map.removeLayer(layerId)
          if (onClickLayer) {
            map.off('click', layerId, onClickLayer)
          }
        }
      }
      if (map.getSource(sourceId)) {
        map.removeSource(sourceId)
      }
    }
  }, [map])

  // initializeOrUpdate
  useEffect(() => {
    if (!geoData || !map) {
      return
    }

    const source: GeoJSONSource = map.getSource(sourceId) as GeoJSONSource
    if (source && geoData) {
      source.setData(geoData)
    } else {
      map.addSource(sourceId, {
        type: 'geojson',
        data: geoData,
      })
    }

    const existingLayer = map.getLayer(layerId)
    if (!existingLayer) {
      const layer: MapBoxGL.Layer = {
        id: layerId,
        type: 'fill',
        source: sourceId,
        ...layerOptions,
      }
      map.addLayer(layer)

      if (onClickLayer) {
        map.on('click', layerId, onClickLayer)
      }
    }

    if (layerOptions.layout) {
      // Update layout properties
      map.setLayoutProperty(layerId, 'visibility', layerOptions.layout.visibility)
    }
    if (layerOptions.paint) {
      // Update paint properties
      map.setPaintProperty(layerId, 'fill-color', layerOptions.paint['fill-color'])
      map.setPaintProperty(layerId, 'fill-outline-color', layerOptions.paint['fill-outline-color'])
      map.setPaintProperty(layerId, 'fill-opacity', layerOptions.paint['fill-opacity'])
    }
  }, [geoData, layerOptions])

  return null
}

export default GeoJsonLayer
